import { Component, effect, input, output, signal, Signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormInputComponent } from '../../utils/form-input/form-input.component';
import { TranslateModule } from '@ngx-translate/core';
import { SettingModel } from '../../../models/resources/setting.model';
import {LocationModel} from "../../../models/resources/location.model";
import {LocationService} from "../../../services/resources/location.service";
import {LinkSelectComponent} from "../../utils/link-select/link-select.component";

@Component({
  selector: 'app-setting-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormInputComponent,
    TranslateModule,
    LinkSelectComponent
  ],
  templateUrl: './setting-form.component.html',
  styleUrl: './setting-form.component.scss',
})
export class SettingFormComponent {
  formGroupOutput = output<FormGroup>();
  formGroup: Signal<FormGroup>;
  setting = input<SettingModel>();
  selectedLocation = signal<LocationModel | undefined>(undefined);

  constructor(
    private formBuilder: FormBuilder,
    public locationService: LocationService,
  ) {
    this.formGroup = signal(this.formBuilder.group({
      name: [null, [Validators.required]],
      averageKmBus: [null, [Validators.required]],
      maxKmSoft: [null, [Validators.required]],
      maxKmHard:[null, [Validators.required]],
      maxDrivingTimeSoft: [null, [Validators.required]],
      maxDrivingTimeHard: [null, [Validators.required]],
      totalDrivingTimeSoft: [null, [Validators.required]],
      pauseAmount: [null, [Validators.required]],
      pauseDuration: [null, [Validators.required]],
      bufferTime: [null, [Validators.required]],
      busCapacity: [null, [Validators.required]],
      uebergabeStopDuration: [null, [Validators.required]],
      wechselStopDuration: [null, [Validators.required]],
      abholungStopDuration: [null, [Validators.required]],
      startTime: [null, [Validators.required]],
      startDayOffset: [null, [Validators.required]],
      roOriginLatitude: [null, [
        Validators.required,
        Validators.min(-90),
        Validators.max(90),
        Validators.pattern(/^-?\d{1,2}([.,]\d+)?$/),
      ],],
      roOriginLongitude: [null, [
        Validators.required,
        Validators.min(-180),
        Validators.max(180),
        Validators.pattern(/^-?\d{1,3}([.,]\d+)?$/),
      ],],
      roOriginLabel: [null, []],
      hubStopsCount:[null, [Validators.required]],
      hubStopDuration: [null, [Validators.required]],
      gasStopDuration: [null, [Validators.required]],
      gasStopAmount: [null, [Validators.required]],
      hubMaxWaitTime: [null, [Validators.required]],
      employeeStartTime: [null, [Validators.required]],
      employeeEndTime: [null, [Validators.required]],
      employeeBreakDuration: [null, [Validators.required]],
      hollensteinStopDuration: [null, [Validators.required]],
      kilometerTriggerRadius: [null, [Validators.required]],
      locationStop: [null, [Validators.required]]
    }));

    effect(() => {
    this.formGroupOutput.emit(this.formGroup());
    });

    effect(() => {
    if (this.setting())
      this.updateFormValues();
    });

    this.formGroup()
      .get('location')
      ?.valueChanges.subscribe({
      next: (value) => {
        this.selectedLocation.set(value);
      },
    });
  }

  updateFormValues() {
    const currentModel = this.setting();

    if(currentModel) {
      this.formGroup().patchValue({
        name: currentModel.name,
        averageKmBus: currentModel.averageKmBus,
        maxKmSoft: currentModel.maxKmSoft,
        maxKmHard: currentModel.maxKmHard,
        maxDrivingTimeSoft: currentModel.maxDrivingTimeSoft,
        maxDrivingTimeHard: currentModel.maxDrivingTimeHard,
        totalDrivingTimeSoft: currentModel.totalDrivingTimeSoft,
        pauseAmount: currentModel.pauseAmount,
        pauseDuration: currentModel.pauseDuration,
        bufferTime: currentModel.bufferTime,
        busCapacity: currentModel.busCapacity,
        uebergabeStopDuration: currentModel.uebergabeStopDuration,
        wechselStopDuration: currentModel.wechselStopDuration,
        abholungStopDuration: currentModel.abholungStopDuration,
        startTime: currentModel.startTime?.substring (0,5),
        startDayOffset: currentModel.startDayOffset,
        roOriginLatitude: currentModel.roOriginLatitude,
        roOriginLongitude: currentModel.roOriginLongitude,
        roOriginLabel: currentModel.roOriginLabel,
        hubStopsCount: currentModel.hubStopsCount,
        hubStopDuration: currentModel.hubStopDuration,
        gasStopDuration: currentModel.gasStopDuration,
        gasStopAmount: currentModel.gasStopAmount,
        hubMaxWaitTime: currentModel.hubMaxWaitTime,
        employeeStartTime: currentModel.employeeStartTime?.substring (0,5),
        employeeEndTime: currentModel.employeeEndTime?.substring (0,5),
        employeeBreakDuration: currentModel.employeeBreakDuration,
        locationStop: currentModel.locationStop?.id,
        hollensteinStopDuration: currentModel.hollensteinStopDuration,
        kilometerTriggerRadius: currentModel.kilometerTriggerRadius
      });
    }
  }
}

